import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch, apiFetchEmpty } from './client';
import { invalidateCommunityAudit } from './communityAudit';

const EligibleSupplierSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
});

const SupplierSlotSchema = z.object({
  sub_phase_id: z.coerce.number(),
  sub_phase_name: z.string(),
  phase_id: z.coerce.number(),
  phase_name: z.string(),
  phase_sort: z.coerce.number().optional(),
  sub_phase_sort: z.coerce.number().optional(),
  eligible_suppliers: z.array(EligibleSupplierSchema),
  supplier: z.string().nullable(),
  error: z.string().nullable(),
});

const SupplierSlotsResponseSchema = z.object({
  slots: z.array(SupplierSlotSchema),
});

export type CommunitySupplierSlot = z.infer<typeof SupplierSlotSchema>;

export function useSupplierSlots() {
  return useQuery({
    queryKey: ['supplier-slots'],
    staleTime: 60_000,
    queryFn: () => apiFetch('/api/supplier-slots', SupplierSlotsResponseSchema),
  });
}

export function useCommunitySuppliers(communityId: string | undefined) {
  return useQuery({
    queryKey: ['communities', communityId, 'suppliers'],
    enabled: !!communityId,
    queryFn: () =>
      apiFetch(`/api/communities/${communityId}/suppliers`, SupplierSlotsResponseSchema),
  });
}

export function useSaveCommunitySuppliers(communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (suppliers: Record<string, string>) =>
      apiFetchEmpty(`/api/communities/${communityId}/suppliers`, {
        method: 'POST',
        body: JSON.stringify({ suppliers }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['communities', communityId, 'suppliers'] });
      void qc.invalidateQueries({ queryKey: ['communities', communityId ?? '', 'bid'] });
      invalidateCommunityAudit(qc, communityId);
    },
  });
}
